import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { projectService } from '../services/api';
import { 
  Briefcase, 
  DollarSign, 
  Clock, 
  MapPin, 
  Search, 
  Filter,
  Calendar,
  Users,
  Star,
  Edit,
  Trash2,
  Plus,
  AlertCircle,
  ArrowLeft
} from 'lucide-react';

interface Project {
  id: string;
  title: string;
  description: string;
  category: string;
  skills: string[];
  budget: {
    type: 'hourly' | 'fixed';
    min: number;
    max: number;
  };
  timeline: string;
  location: string;
  experience_level: string;
  client: {
    name: string;
    rating?: number;
    projects_completed?: number;
  };
  posted_date: string;
  proposals_count: number;
  status: string;
}

const ClientProjects: React.FC = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    if (user?.userType !== 'client') {
      navigate('/');
      return;
    }
    loadProjects();
  }, [user, navigate]);

  useEffect(() => {
    filterProjects();
  }, [projects, searchTerm, statusFilter]);

  const loadProjects = async () => {
    try {
      setIsLoading(true);
      const data = await projectService.getByClient(user?.email || '');
      
      // Transform the data to match our interface
      const transformedProjects = data.map((project: any) => ({
        id: project.id,
        title: project.title,
        description: project.description,
        category: project.category,
        skills: project.skills || [],
        budget: project.budget,
        timeline: project.timeline,
        location: project.location,
        experience_level: project.experience_level,
        client: {
          name: project.client_name || 'Unknown Client',
          rating: 4.5, // Default rating for now
          projects_completed: 1, // Default count for now
        },
        posted_date: project.posted_date,
        proposals_count: project.proposals_count || 0,
        status: project.status || 'open',
      }));
      
      setProjects(transformedProjects);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load projects');
      console.error('Error loading projects:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const filterProjects = () => {
    let filtered = projects;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(project =>
        project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter) {
      filtered = filtered.filter(project => project.status === statusFilter);
    }

    setFilteredProjects(filtered);
  };

  const handleEditProject = (projectId: string) => {
    // Navigate to edit project page (to be implemented)
    navigate(`/edit-project/${projectId}`);
  };

  const handleDeleteProject = async (projectId: string) => {
    if (window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      try {
        await projectService.updateStatus(projectId, 'cancelled');
        // Reload projects after deletion
        loadProjects();
      } catch (err) {
        console.error('Error deleting project:', err);
        alert('Failed to delete project. Please try again.');
      }
    }
  };

  const formatBudget = (budget: Project['budget']) => {
    if (budget.type === 'hourly') {
      return `$${budget.min}-${budget.max}/hr`;
    }
    return `$${budget.min}-${budget.max}`;
  };

  const formatTimeline = (timeline: string) => {
    const timelineMap: { [key: string]: string } = {
      'less_than_1_month': 'Less than 1 month',
      '1_to_3_months': '1 to 3 months',
      '3_to_6_months': '3 to 6 months',
      'more_than_6_months': 'More than 6 months'
    };
    return timelineMap[timeline] || timeline;
  };

  const getStatusColor = (status: string) => {
    const statusColors: { [key: string]: string } = {
      'open': 'bg-green-100 text-green-800',
      'in_progress': 'bg-blue-100 text-blue-800',
      'completed': 'bg-gray-100 text-gray-800',
      'cancelled': 'bg-red-100 text-red-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status: string) => {
    const statusLabels: { [key: string]: string } = {
      'open': 'Open',
      'in_progress': 'In Progress',
      'completed': 'Completed',
      'cancelled': 'Cancelled'
    };
    return statusLabels[status] || status;
  };

  if (!user || user.userType !== 'client') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle size={40} className="mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">This page is only accessible to clients.</p>
          <button
            onClick={() => navigate('/')}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-white/70">Loading your projects...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle size={40} className="mx-auto text-red-400 mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Error</h2>
          <p className="text-white/70 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-gray-800/50 backdrop-blur-sm border-b border-white/10 shadow-lg">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate('/')}
                  className="flex items-center space-x-2 text-white/70 hover:text-white"
                >
                  <ArrowLeft size={20} />
                  <span>Back</span>
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-white">My Posted Projects</h1>
                  <p className="text-white/70">Manage and track your project postings</p>
                </div>
              </div>
              <Link
                to="/post-project"
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md flex items-center space-x-2"
              >
                <Plus size={16} />
                <span>Post New Project</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="bg-gray-800/50 backdrop-blur-sm border border-white/10 rounded-lg shadow-lg p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Search */}
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40" />
                <input
                  type="text"
                  placeholder="Search projects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-white/20 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white/5 text-white placeholder-white/50"
                />
              </div>

              {/* Status Filter */}
              <div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-white/20 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white/5 text-white"
                >
                  <option value="">All Statuses</option>
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              {/* Project Count */}
              <div className="flex items-center justify-center md:justify-end">
                <span className="text-sm text-white/60">
                  {filteredProjects.length} of {projects.length} projects
                </span>
              </div>
            </div>
          </div>

          {/* Projects List */}
          {filteredProjects.length === 0 ? (
            <div className="bg-gray-800/50 backdrop-blur-sm border border-white/10 rounded-lg shadow-lg p-12 text-center">
              <Briefcase size={48} className="mx-auto text-white/40 mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No projects found</h3>
              <p className="text-white/70 mb-6">
                {searchTerm || statusFilter 
                  ? 'Try adjusting your search or filter criteria.'
                  : "You haven't posted any projects yet."
                }
              </p>
              {!searchTerm && !statusFilter && (
                <Link
                  to="/post-project"
                  className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-md inline-flex items-center space-x-2"
                >
                  <Plus size={16} />
                  <span>Post Your First Project</span>
                </Link>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredProjects.map((project) => (
                <div key={project.id} className="bg-white rounded-lg shadow p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
                    {/* Project Info */}
                    <div className="flex-1 mb-4 lg:mb-0">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-lg font-semibold text-gray-900">{project.title}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                          {getStatusLabel(project.status)}
                        </span>
                      </div>
                      
                      <p className="text-gray-600 mb-4 line-clamp-2">{project.description}</p>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div className="flex items-center space-x-2">
                          <Briefcase size={16} className="text-gray-400" />
                          <span className="text-gray-600">{project.category}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <DollarSign size={16} className="text-gray-400" />
                          <span className="text-gray-600">{formatBudget(project.budget)}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Clock size={16} className="text-gray-400" />
                          <span className="text-gray-600">{formatTimeline(project.timeline)}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <MapPin size={16} className="text-gray-400" />
                          <span className="text-gray-600 capitalize">{project.location}</span>
                        </div>
                      </div>

                      {project.skills.length > 0 && (
                        <div className="mt-4">
                          <h4 className="text-sm font-medium text-gray-900 mb-2">Required Skills:</h4>
                          <div className="flex flex-wrap gap-2">
                            {project.skills.slice(0, 5).map((skill, index) => (
                              <span
                                key={index}
                                className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                              >
                                {skill}
                              </span>
                            ))}
                            {project.skills.length > 5 && (
                              <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                                +{project.skills.length - 5} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Project Stats and Actions */}
                    <div className="flex flex-col items-end space-y-4">
                      <div className="text-right">
                        <div className="flex items-center space-x-2 mb-1">
                          <Calendar size={16} className="text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {new Date(project.posted_date).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2 mb-1">
                          <Users size={16} className="text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {project.proposals_count} proposals
                          </span>
                        </div>
                      </div>

                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditProject(project.id)}
                          className="px-3 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md flex items-center space-x-1"
                        >
                          <Edit size={14} />
                          <span>Edit</span>
                        </button>
                        <button
                          onClick={() => handleDeleteProject(project.id)}
                          className="px-3 py-2 text-sm text-red-700 bg-red-100 hover:bg-red-200 rounded-md flex items-center space-x-1"
                        >
                          <Trash2 size={14} />
                          <span>Delete</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientProjects;
